"use client"
import useMediaGetter from "@/app/hooks/useMediaGetter";
import Banner from "../shared/Banner";
import BannerPlaceholder from "../shared/BannerPlaceholder";
import OSectionWithNoImages from "./OSectionWithNoImages";
import { useContext, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { loadingContext } from "@/app/store/context/isLoadingContext";

const MID_TABLET_QUERY = "(min-width: 660px) and (max-width: 999px)";
// Matches .about-us-page accordion height (56.4rem) with the same ~6.4rem peek as 78.4 / 72
const MID_TABLET_ACCORDION_GAP = 52;
const DEFAULT_MOBILE_ACCORDION_GAP = 72;

const AccordionSection = dynamic(() => import("./AccordionSection"), {
  loading: () => (
    <div className="min-h-[78.4rem] lg:min-h-[48.8rem]" aria-hidden />
  ),
});

const LifeAsItShouldBe = dynamic(() => import("./LifeAsItShouldBe"), {
  ssr: false,
  loading: () => (
    <div
      className="life-as-it-should-be-container w-full aspect-[1/2.1] lg:aspect-[1.6/1] min-h-[20rem] bg-oBlue-150"
      aria-hidden
    />
  ),
});

const AboutUsComponent = ({ banner, about, accordion, lifeAsItShouldBe, initialBannerImageSrc }: any) => {
  const {isPageFetched, setIsPageFetched} = useContext(loadingContext);
  const { imgSrc, videoSrc } = useMediaGetter(banner?.media)
  const bannerImageSrc = imgSrc ?? initialBannerImageSrc;
  const [isMidTablet, setIsMidTablet] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MID_TABLET_QUERY);
    const sync = () => setIsMidTablet(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  
  useEffect(()=> {
    if(bannerImageSrc) {
      setTimeout(()=> {
        setIsPageFetched((prev: string)=> {
          if(prev === "loading") return "fetched";
          return prev;
        })
      })
    }
  }, [bannerImageSrc, setIsPageFetched])

  const tl = useRef<GSAPTimeline | null>(null);
  useEffect(() => {
    if (isPageFetched === "fetched" && bannerImageSrc) {
      let cancelled = false;

      const timer = setTimeout(async () => {
        const { default: gsap } = await import("gsap");
        if (cancelled) return;

        const html = document.documentElement;

        tl.current?.kill();
        tl.current = gsap.timeline();
        tl.current
        .timeScale(html.classList.contains("loaded") ? 1.25 : 1)
        .set(".banner .hero-title-word", { yPercent: 50, alpha: 0 })
        .set(".banner .transparent-o", { yPercent: 30 })
        .set(".banner .hero-video", { clipPath: "inset(50% 50% 50% 50%)", scale: 0.75, transformOrigin: "center center" })
        .to(".banner .hero-image img", {
          delay: html.classList.contains("loaded") ? 0.95 : 2.2,
          duration: 1,
          ease: "power2.inOut",
          y: "0%"
        })
        .to(".banner .hero-image-wrapper", {
          duration: 1,
          ease: "power2.inOut",
          scale: 1,
        }, "-=0.25")
        .to(".navbar_section", {
          duration: 1,
          ease: "power2.inOut",
          y: "0%"
        }, "-=0.4")
        .to(".banner .hero-video", {
          duration: 1,
          ease: "expo.inOut",
          scale: 1,
          clipPath: "inset(0% 0% 0% 0%)",
        }, "<")
        .to(".banner .hero-title-word", {
          duration: 1,
          stagger: 0.05,
          ease: "expo.inOut",
          yPercent: 0,
          alpha: 1
        }, "<")
        .to(".banner .transparent-o", {
          duration: 1,
          stagger: 0.05,
          ease: "expo.out",
          yPercent: 0,
        }, "<");
      }, 50);

      return () => {
        cancelled = true;
        clearTimeout(timer);
        tl.current?.kill();
      };
    }
  }, [isPageFetched, bannerImageSrc]);

  return (
    <div className="page_wrapper about-us-page">
      {bannerImageSrc ? (
        <Banner
          priority
          optimizedImage
          imageSrc={`${bannerImageSrc}`}
          title={banner?.title?.en}
          oTitle={about?.title}
          oDescription={about?.description}
          description={banner?.description?.en}
          videoSrc={videoSrc || "/owest-banner.mp4"}
          videoWidth="21.125rem"
          videoHeight="13.6875rem"
          minHeight={"86vh"}
          videoPosition={"1rem"}
          oFill="#fff"
        />
      ) : (
        <BannerPlaceholder minHeight="86vh" />
      )}
      <OSectionWithNoImages
        title="The Core of O West"
        staticTitle
        customClass="z-[-1] relative -mb-[8rem] lg:-mb-[17.5rem] -mt-[1px]"
      />
      <AccordionSection
        data={accordion}
        gapValue={42}
        gapValueMobile={isMidTablet ? MID_TABLET_ACCORDION_GAP : DEFAULT_MOBILE_ACCORDION_GAP}
        optimizedImage
      />
      <LifeAsItShouldBe data={lifeAsItShouldBe} />
    </div>
  );
}

export default AboutUsComponent;